// Third Party
import * as React from 'react';
import { ReactElement, cloneElement } from 'react';
import { twMerge } from 'tailwind-merge';

// Utils
import { WithChildrenCommonProps } from '@types';
import { Spinner, SpinnerSize } from '@components/spinner';
import { getButtonComponentStylesAsClassName } from './button-styles';
import { ButtonColor, ButtonSize, OutlinedButtonColor } from './utils';
import cx from 'classnames';
interface ButtonBaseProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** If the button will take up the full width of its container */
  isFullWidth?: boolean;
  /** Boolean indicating if boolean is circle shape */
  isCircle?: boolean;
  /** Sets loading state */
  isLoading?: boolean;
  /** Size of the button */
  size?: ButtonSize;
  /** Spinner you want to render when loading if not the default one */
  spinner?: React.ReactNode;
  /** Icon to display on the right side of the button text */
  rightIcon?: React.ReactNode;
}
interface OutlinedButtonProps extends ButtonBaseProps {
  /** The variant theme for the button */
  variant: 'outlined';
  /** Color of the button (secondary not available for outlined) */
  color?: OutlinedButtonColor;
}
interface NonOutlinedButtonProps extends ButtonBaseProps {
  /** The variant theme for the button */
  variant?: 'filled' | 'text';
  /** Color of the button */
  color?: ButtonColor;
}
export type ButtonExtendProps = OutlinedButtonProps | NonOutlinedButtonProps;
export type ButtonProps = WithChildrenCommonProps<ButtonExtendProps>;
export const Button = React.forwardRef((props: ButtonProps, ref: React.ForwardedRef<HTMLButtonElement>): JSX.Element => {
  const {
    isFullWidth = false,
    children,
    color = 'default',
    isCircle = false,
    isLoading = false,
    size = 'md',
    spinner = <Spinner size={SpinnerSize.SIZE_SMALL} />,
    variant = 'filled',
    rightIcon,
    dataId,
    disabled = false,
    type = 'button',
    className: classNameProp,
    ...rest
  } = props;
  const spinnerWithChangedClassName = spinner && cloneElement(spinner as ReactElement<{
    className?: string;
  }>, {
    className: cx({
      // Filled buttons: match text color for spinner
      // Default/secondary filled uses primary text color
      'text-primary-main': variant === 'filled' && (color === 'default' || color === 'secondary'),
      'text-secondary-main': variant === 'filled' && color === 'primary',
      'text-white': variant === 'filled' && (color === 'gray' || color === 'black'),
      // Outlined/text buttons: match the button color (default matches primary)
      'text-primary-dark': variant !== 'filled' && (color === 'default' || color === 'primary' || color === 'black'),
      'text-secondary-dark': variant !== 'filled' && color === 'secondary',
      'text-gray-dark': variant !== 'filled' && color === 'gray'
    })
  });
  const Loading = (): JSX.Element => <span className="absolute inset-0 flex items-center justify-center">
        {spinnerWithChangedClassName}
      </span>;

  // Arbitrary variant css selector:
  // https://tailwindcss.com/docs/hover-focus-and-other-states#using-arbitrary-variants
  const iconSelectorStyles = '[&_.icon+span]:ml-2 [&_span+.icon]:ml-2';
  const tailwindClassName = twMerge(getButtonComponentStylesAsClassName(isFullWidth, color, isCircle, size, variant, disabled, isLoading), iconSelectorStyles, classNameProp);
  return <button data-id={dataId} className={tailwindClassName} ref={ref} disabled={disabled} type={type} {...rest}>
        <>
          {!!children && children}
          {rightIcon && <span className={cx('inline-flex shrink-0', {
        'ml-2': !!children
      })}>
              {rightIcon}
            </span>}
          {isLoading && <Loading />}
        </>
      </button>;
});
Button.displayName = 'Button';